import React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  PAPER_SIZE_LABELS,
  POSTAGE_TYPE_LABELS,
} from "common/services/mailingsService/constants";
import { PaperSize, PostageType } from "../../../generated/graphql";
import { spacing } from "../../../stitches.config";
import { Label, Switch } from "../../Inputs";
import { ReactHookFormSelect } from "../../Inputs/Select";
import { FlexColumn, FlexRow } from "../__styles__/Layout";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
} from "../__styles__/Modal";
import { Button } from "../Button";
import { Title } from "../Typography";
import { SwitchFieldset } from "./__styles__/MailingForm";

export const POSTAGE_TYPE_OPTIONS = [
  {
    value: PostageType.USPS_STANDARD,
    label: POSTAGE_TYPE_LABELS[PostageType.USPS_STANDARD],
  },
  {
    value: PostageType.USPS_FIRST_CLASS,
    label: POSTAGE_TYPE_LABELS[PostageType.USPS_FIRST_CLASS],
  },
  {
    value: PostageType.USPS_CERTIFIED,
    label: POSTAGE_TYPE_LABELS[PostageType.USPS_CERTIFIED],
  },
];

export const PAPER_SIZE_OPTIONS = [
  { value: PaperSize.US_LETTER, label: PAPER_SIZE_LABELS[PaperSize.US_LETTER] },
];

export type MailingFormData = {
  postageType: PostageType;
  paperSize: PaperSize;
  printInColor: boolean;
  doubleSided: boolean;
};

type MailingFormProps = {
  onSubmit: (data: MailingFormData) => void;
  onCancel: () => void;
};

export function MailingForm({ onSubmit, onCancel }: MailingFormProps) {
  const { control, handleSubmit } = useForm<MailingFormData>({
    defaultValues: {
      postageType: PostageType.USPS_STANDARD,
      paperSize: PaperSize.US_LETTER,
      printInColor: false,
      doubleSided: false,
    },
  });

  const onFormSubmit = handleSubmit(onSubmit);

  return (
    <Container width="narrow" css={{ "@md": { maxHeight: "unset" } }}>
      <HeaderSection>
        <FlexRow
          css={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Title size="small" type="semiBold">
            Send mail
          </Title>
        </FlexRow>
      </HeaderSection>
      <ContentSection css={{ padding: `${spacing.l}` }}>
        <FormSection>
          <FlexColumn gap="m">
            <div>
              <Label text="Postage type" size="large" required={true} />
              <ReactHookFormSelect
                name="postageType"
                control={control}
                options={POSTAGE_TYPE_OPTIONS}
                required
                readonly={POSTAGE_TYPE_OPTIONS.length === 1}
              />
            </div>

            <div>
              <Label text="Paper size" size="large" required={true} />
              <ReactHookFormSelect
                name="paperSize"
                control={control}
                options={PAPER_SIZE_OPTIONS}
                required
                readonly={PAPER_SIZE_OPTIONS.length === 1}
              />
            </div>
          </FlexColumn>

          <SwitchFieldset>
            <Label text="Print in color" size="large" />
            <Controller
              control={control}
              name="printInColor"
              render={({ field }) => (
                <Switch
                  name="printInColor"
                  labels="yes/no"
                  value={field.value ?? false}
                  onChange={field.onChange}
                  size="small"
                />
              )}
            />
          </SwitchFieldset>

          <SwitchFieldset>
            <Label text="Double-sided" size="large" />
            <Controller
              control={control}
              name="doubleSided"
              render={({ field }) => (
                <Switch
                  name="doubleSided"
                  labels="yes/no"
                  value={field.value ?? false}
                  onChange={field.onChange}
                  size="small"
                />
              )}
            />
          </SwitchFieldset>
        </FormSection>
      </ContentSection>
      <ButtonSection>
        <PrimaryButtons>
          <Button
            type="button"
            styleVariant="secondary"
            onClick={onCancel}
            size="medium"
          >
            Cancel
          </Button>
          <Button
            type="button"
            styleVariant="primary"
            onClick={onFormSubmit}
            size="medium"
          >
            Send
          </Button>
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
}
